import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { panelsApi } from '../api';
import { FiArrowLeft, FiExternalLink, FiPower, FiUsers, FiClock, FiServer, FiShield, FiDollarSign, FiGlobe, FiBarChart2, FiActivity, FiUser } from 'react-icons/fi';
import toast from 'react-hot-toast';

export default function PanelDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPanel = async () => {
    try {
      const { data } = await panelsApi.getById(id);
      setPanel(data.panel);
    } catch (err) {
      toast.error('Panel not found');
      navigate('/panels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPanel(); }, [id]);

  const handleToggle = async () => {
    try {
      await panelsApi.toggle(id);
      toast.success('Panel status toggled');
      fetchPanel();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const handleRenew = async () => {
    try {
      await panelsApi.renew(id);
      toast.success('Panel renewed for 30 days');
      fetchPanel();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to renew');
    }
  };

  const handleRetry = async () => {
    try {
      await panelsApi.retry(id);
      toast.success('Retry initiated!');
      fetchPanel();
    } catch (err) {
      toast.error('Failed to retry deployment');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!panel) return null;

  return (
    <DashboardLayout>
      <button onClick={() => navigate('/panels')}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-sm font-medium transition-colors mb-6">
        <FiArrowLeft size={14} /> Back to Panels
      </button>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">{panel.panel_name || panel.domain}</h1>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
            panel.status === 'active' ? 'bg-secondary text-secondary-foreground border-transparent' :
            panel.status === 'suspended' ? 'bg-muted text-muted-foreground border-transparent' :
            panel.status === 'pending' || panel.status === 'deploying' ? 'bg-secondary text-secondary-foreground border-transparent' :
            'bg-destructive/10 text-destructive border-transparent'
          }`}>
            {panel.status.charAt(0).toUpperCase() + panel.status.slice(1)}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {panel.status === 'pending' && (
            <button onClick={handleRetry} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors bg-secondary text-secondary-foreground hover:bg-secondary/80">
              <FiActivity size={14} /> Retry Deployment
            </button>
          )}
          <button onClick={handleRenew}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground">
            <FiClock size={14} /> Renew (30 Days)
          </button>
          <button onClick={() => navigate(`/panels/${panel.id}/admins`)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground">
            <FiUsers size={14} /> Manage Accounts
          </button>
          <button onClick={handleToggle}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground">
            <FiPower size={14} /> {panel.status === 'active' ? 'Suspend' : 'Activate'}
          </button>
        </div>
      </div>

      {(panel.deploy_log || panel.deployLog) && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold tracking-tight text-foreground mb-3">Deployment Logs</h3>
          <div className="bg-[#1E1E1E] rounded-md p-4 text-[#A7B6C2] font-mono text-xs whitespace-pre-wrap max-h-[300px] overflow-y-auto border border-[#333]">
            {panel.deploy_log || panel.deployLog}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Orders', value: (panel.total_orders || 0).toLocaleString(), icon: FiBarChart2 },
          { label: 'Total Users', value: (panel.total_users || 0).toLocaleString(), icon: FiUsers },
          { label: 'Revenue', value: panel.currency && panel.currency !== 'USD' ? `$${Number(panel.total_revenue_usd || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} (${Number(panel.total_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} ${panel.currency})` : `$${Number(panel.total_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, icon: FiDollarSign },
          { label: 'Admin Accounts', value: `${panel.admin_count || 0} / ${panel.admin_accounts_limit || 5}`, icon: FiShield }
        ].map((stat, i) => (
          <div key={i} className="card p-4 flex flex-col items-center justify-center text-center">
            <stat.icon size={20} className="text-muted-foreground mb-2" />
            <div className="text-2xl font-bold tracking-tight text-foreground">{stat.value}</div>
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mt-1">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="card p-6">
        <h3 className="text-lg font-semibold tracking-tight text-foreground mb-4">Panel Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><FiGlobe size={12} />Domain</span>
            <span className="text-sm font-medium text-foreground">
              <a href={`https://${panel.domain}`} target="_blank" rel="noreferrer" className="hover:underline flex items-center gap-1">
                {panel.domain} <FiExternalLink size={12} />
              </a>
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><FiDollarSign size={12} />Currency</span>
            <span className="text-sm font-medium text-foreground">{panel.currency || 'USD'}</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><FiServer size={12} />PHP Version</span>
            <span className="text-sm font-medium text-foreground">PHP {panel.php_version || '8.2'}</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><FiShield size={12} />SSL Status</span>
            <span className="text-sm font-medium text-foreground">{panel.ssl_status || 'none'}</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><FiClock size={12} />Created</span>
            <span className="text-sm font-medium text-foreground">{new Date(panel.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><FiClock size={12} />Expiry Date</span>
            <span className={`text-sm font-medium ${panel.expires_at && new Date(panel.expires_at) < new Date() ? 'text-destructive font-semibold' : 'text-foreground'}`}>
              {panel.expires_at ? new Date(panel.expires_at).toLocaleString() : '—'}
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><FiActivity size={12} />Last Admin Login</span>
            <span className="text-sm font-medium text-foreground">
              {panel.last_admin_login_at ? new Date(panel.last_admin_login_at).toLocaleString() : 'Never'}
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><FiUser size={12} />Admin Username</span>
            <span className="text-sm font-medium text-foreground">{panel.child_admin_username || 'Not set'}</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><FiServer size={12} />Database</span>
            <span className="text-xs font-medium text-foreground break-all">{panel.db_name}</span>
          </div>
        </div>
        {panel.notes && (
          <div className="mt-6 p-4 bg-muted/50 rounded-md border border-border text-muted-foreground text-sm">
            {panel.notes}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
